// The arena skin button and picker (territory-ui.md §7, territory-visuals.md §14; backlog SK-2).
// Behaviour follows the reference's flow (a square skin button right of Play showing the current
// skin; choosing applies at once and is remembered), laid out as ours: a modal with a grid of
// live previews ("Plain" first, then the 16 catalogue skins in order), each a canvas that turns
// its head slowly while the picker is open. Click, Enter or Space picks; the arrow keys move
// through the grid (clamped at the ends, like the reference's arrows); Done or Esc closes. The
// pick lives in the settings store (`arenaSkin`, so localStorage `ballio.settings`) and goes out
// in PLAY to arena rooms. Copy is ours (i18n `arena.skin.*`, `arena.skins.*`); every preview is
// drawn by arena/skins.ts from our own designs.
//
// The view rules (items, names, keyboard moves, preview colours) are pure functions below
// (tests); the DOM parts run in the browser only.

import {
  ARENA_PALETTE,
  ARENA_SKINS,
  arenaSkinById,
  normalizeArenaSkinId,
  type Rgb,
} from '@ballio/shared';
import { drawSkinPreview, SkinTileCache } from '../../arena/skins.js';
import type { UiClickSource } from '../../events/bus.js';
import { get2d } from '../../render/canvas.js';
import type { SettingsStore } from '../../settings/store.js';
import { h } from '../dom.js';
import { hasKey, t } from '../i18n/t.js';
import { type ModalHandle, openModal } from '../modals/modal.js';

/** Picker entries: `""` (plain) first, then the catalogue in order. */
export function skinPickerIds(): string[] {
  return ['', ...ARENA_SKINS.map((s) => s.id)];
}

/** The display name of a skin id (ours, from i18n); unknown ids read as plain. */
export function skinDisplayName(id: string): string {
  const skin = arenaSkinById(id);
  if (!skin) return t('arena.skin.none');
  const key = `arena.skin.${skin.slug}`;
  return hasKey(key) ? t(key) : skin.name;
}

/**
 * The preview colour of entry `index`: a different palette colour per skin, so the grid shows
 * the range (the real colour is picked per life by the server).
 */
export function previewColor(index: number): Rgb {
  const n = ARENA_PALETTE.length;
  return ARENA_PALETTE[(((index * 11 + 4) % n) + n) % n] as Rgb;
}

/** The button's preview colour: our green. */
export const BUTTON_PREVIEW_COLOR: Rgb = 0x3dbe5a;

/**
 * Where an arrow / Home / End key moves the selection in a grid of `count` entries, `cols` wide
 * (clamped at the ends, no wrap); null for other keys.
 */
export function pickerMove(index: number, key: string, cols: number, count: number): number | null {
  const c = Math.max(1, cols);
  let next: number;
  switch (key) {
    case 'ArrowLeft':
      next = index - 1;
      break;
    case 'ArrowRight':
      next = index + 1;
      break;
    case 'ArrowUp':
      next = index - c;
      break;
    case 'ArrowDown':
      next = index + c;
      break;
    case 'Home':
      next = 0;
      break;
    case 'End':
      next = count - 1;
      break;
    default:
      return null;
  }
  // Up / Down past an edge stay put; the others clamp to the ends (no wrap).
  if (next < 0 || next >= count) {
    if (key === 'ArrowUp' || key === 'ArrowDown') return index;
    return Math.min(count - 1, Math.max(0, next));
  }
  return next;
}

export interface SkinPickerDeps {
  readonly settings: SettingsStore;
  playClick?(source: UiClickSource): void;
  /** Device px per CSS px for the previews. */
  dpr(): number;
}

export interface SkinButton {
  readonly el: HTMLButtonElement;
  /** Redraws the button's preview (the pick changed elsewhere). */
  refresh(): void;
  destroy(): void;
}

interface Preview {
  readonly canvas: HTMLCanvasElement;
  readonly size: number;
  skinId: string;
  color: Rgb;
}

function makePreview(size: number, skinId: string, color: Rgb): Preview {
  const canvas = h('canvas', {
    class: 't-skin-canvas',
    'aria-hidden': 'true',
  }) as HTMLCanvasElement;
  canvas.style.setProperty('--s', `${size}px`);
  return { canvas, size, skinId, color };
}

function paint(p: Preview, tiles: SkinTileCache, dpr: number, nowMs: number): void {
  const px = Math.round(p.size * dpr);
  if (p.canvas.width !== px) p.canvas.width = px;
  if (p.canvas.height !== px) p.canvas.height = px;
  const ctx = get2d(p.canvas);
  if (!ctx) return;
  drawSkinPreview(ctx, tiles, {
    w: p.size,
    h: p.size,
    dpr,
    skinId: p.skinId,
    color: p.color,
    nowMs,
  });
}

/** Opens the picker over the home. */
export function openSkinPicker(deps: SkinPickerDeps, onChange: () => void): ModalHandle {
  const tiles = new SkinTileCache();
  const ids = skinPickerIds();
  const current = (): string => normalizeArenaSkinId(deps.settings.get().arenaSkin);
  const previews: Preview[] = [];
  const buttons: HTMLButtonElement[] = [];

  const select = (i: number, focus: boolean): void => {
    const id = ids[i] ?? '';
    if (id !== current()) {
      deps.settings.set('arenaSkin', id);
      onChange();
    }
    buttons.forEach((b, k) => {
      const on = k === i;
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
    if (focus) buttons[i]?.focus();
  };

  const grid = h('div', {
    class: 't-skin-grid',
    role: 'radiogroup',
    'aria-label': t('arena.skins.title'),
  });
  ids.forEach((id, i) => {
    const p = makePreview(84, id, previewColor(i));
    previews.push(p);
    const b = h(
      'button',
      {
        type: 'button',
        class: 't-skin-item',
        role: 'radio',
        'aria-checked': 'false',
        tabindex: '-1',
        onClick: () => {
          deps.playClick?.('skin');
          select(i, false);
        },
      },
      p.canvas,
      h('span', { class: 't-skin-name' }, skinDisplayName(id)),
    ) as HTMLButtonElement;
    buttons.push(b);
    grid.append(b);
  });
  grid.addEventListener('keydown', (ev: KeyboardEvent) => {
    const at = buttons.findIndex((b) => b === ev.target);
    if (at < 0) return;
    const first = buttons[0];
    const second = buttons[1];
    // Columns from the laid-out grid (responsive).
    const cols =
      first && second && first.offsetTop === second.offsetTop
        ? Math.max(1, buttons.filter((b) => b.offsetTop === first.offsetTop).length)
        : 1;
    const next = pickerMove(at, ev.key, cols, buttons.length);
    if (next === null) return;
    ev.preventDefault();
    select(next, true);
  });

  let handle: ModalHandle | null = null;
  const done = h(
    'button',
    {
      type: 'button',
      class: 't-skin-done',
      onClick: () => {
        deps.playClick?.('close');
        handle?.close();
      },
    },
    t('arena.skins.done'),
  );
  const content = h(
    'div',
    { class: 't-skins' },
    h('h2', { class: 't-skins-title' }, t('arena.skins.title')),
    h('p', { class: 't-skins-hint' }, t('arena.skins.hint')),
    grid,
    done,
  );

  let raf = 0;
  let last = -1e9;
  const loop = (now: number): void => {
    raf = requestAnimationFrame(loop);
    if (now - last < 1000 / 30) return; // 30 fps is plenty for 17 small previews
    last = now;
    const dpr = deps.dpr();
    for (const p of previews) paint(p, tiles, dpr, now);
  };

  handle = openModal(content, {
    label: t('arena.skins.title'),
    className: 'modal-skins',
    onCloseClick: () => deps.playClick?.('close'),
    onClose: () => {
      cancelAnimationFrame(raf);
      tiles.clear();
    },
  });
  const start = Math.max(0, ids.indexOf(current()));
  select(start, false);
  buttons[start]?.scrollIntoView?.({ block: 'nearest' });
  for (const p of previews) paint(p, tiles, deps.dpr(), performance.now());
  raf = requestAnimationFrame(loop);
  return handle;
}

/** The square skin button for the territory home (TerritoryHome.setSkinButton). */
export function createSkinButton(deps: SkinPickerDeps): SkinButton {
  const tiles = new SkinTileCache();
  const preview = makePreview(44, '', BUTTON_PREVIEW_COLOR);
  const el = h(
    'button',
    { type: 'button', class: 't-skin-btn' },
    preview.canvas,
  ) as HTMLButtonElement;
  let picker: ModalHandle | null = null;

  const refresh = (): void => {
    const id = normalizeArenaSkinId(deps.settings.get().arenaSkin);
    preview.skinId = id;
    const label = t('arena.skins.button', { name: skinDisplayName(id) });
    el.setAttribute('aria-label', label);
    el.title = label;
    // A still frame: the head turned a little, so the block reads as a block.
    paint(preview, tiles, deps.dpr(), 900);
  };

  el.addEventListener('click', () => {
    deps.playClick?.('skin');
    if (picker?.open) return;
    picker = openSkinPicker(deps, refresh);
  });
  const off = deps.settings.subscribe((s, prev) => {
    if (s.arenaSkin !== prev.arenaSkin) refresh();
  });
  refresh();
  return {
    el,
    refresh,
    destroy(): void {
      off();
      picker?.close();
      tiles.clear();
    },
  };
}
